
import { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import http from "services/httpClient";

interface TemperatureGaugePropertiesProps {
  show: boolean;
  onClose: () => void;
  config: any;
  deviceId: number;
  refetchGraphs: () => void;
}

interface LabelOption {
  column_index: number;
  label: string;
  unit?: string;
  multiplier?: number;
  offset?: number;
  expression?: string;
  source_column_index?: number;
  aggregation?: string;
  window_hours?: number;
}

export function TemperatureGaugeProperties({
  show,
  onClose,
  config,
  deviceId,
  refetchGraphs,
}: TemperatureGaugePropertiesProps) {
  const [labels, setLabels] = useState<LabelOption[]>([]);
  const [columnIndex, setColumnIndex] = useState<string>("");
  const [unit, setUnit] = useState<"C" | "F">("C");
  const [minTempC, setMinTempC] = useState("0");
  const [maxTempC, setMaxTempC] = useState("100");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show || !deviceId) return;

    let cancelled = false;

    const loadLabels = async () => {
      setLoading(true);
      setError("");

      try {
        const [rawRes, calcRes, statRes] = await Promise.all([
          http.get(`/device/${deviceId}/labels`),
          http.get(`/calculated_params/${deviceId}`),
          http.get(`/stat-definitions/device/${deviceId}`),
        ]);

        const rawLabels: LabelOption[] = (rawRes.data || []).map(
          (item: any) => ({
            ...item,
            column_index: item.column_index,
            label: item.label ?? item.name ?? `Parameter ${item.column_index}`,
          })
        );

        const calculatedLabels: LabelOption[] = (calcRes.data || []).map(
          (item: any) => ({
            ...item,
            column_index: item.column_index,
            label:
              item.result_label ??
              item.label ??
              `Calculated ${item.column_index}`,
            unit: item.unit ?? "",
          })
        );

        const statLabels: LabelOption[] = (statRes.data || []).map(
          (item: any) => ({
            ...item,
            column_index: 1000 + item.id,
            label: item.result_label ?? `Statistic ${item.id}`,
            unit: item.unit ?? "",
          })
        );

        const options = [
          ...rawLabels,
          ...calculatedLabels,
          ...statLabels,
        ];

        if (!cancelled) {
          setLabels(options);

          const savedIndex =
            config.label_column_index ??
            config.column_index ??
            config.device_label?.column_index;

          setColumnIndex(
            savedIndex !== undefined && savedIndex !== null
              ? String(savedIndex)
              : options.length
                ? String(options[0].column_index)
                : ""
          );

          setUnit(config.unit === "F" ? "F" : "C");
          setMinTempC(String(config.min_temp_c ?? 0));
          setMaxTempC(String(config.max_temp_c ?? 100));
        }
      } catch (err) {
        console.error("Failed to load temperature gauge parameters:", err);
        if (!cancelled) {
          setError("Could not load device parameters. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadLabels();

    return () => {
      cancelled = true;
    };
  }, [show, deviceId, config]);

  const handleSave = async () => {
    setError("");

    if (!columnIndex) {
      setError("Please select a device parameter.");
      return;
    }

    const min = Number(minTempC);
    const max = Number(maxTempC);

    if (
      !Number.isFinite(min) ||
      !Number.isFinite(max) ||
      min >= max
    ) {
      setError("The maximum temperature must be greater than the minimum.");
      return;
    }

    const selectedLabel = labels.find(
      (label) => label.column_index === Number(columnIndex)
    );

    if (!selectedLabel) {
      setError("The selected parameter could not be found.");
      return;
    }

    const updatedConfig = {
      ...config,
      type: "temperature_gauge",
      label_column_index: Number(columnIndex),
      column_index: Number(columnIndex),
      title: selectedLabel.label,
      device_label: selectedLabel,
      unit,
      min_temp_c: min,
      max_temp_c: max,
    };

    setSaving(true);

    try {
      await http.patch(`/graphs/${config.id}`, {
        graph_config: updatedConfig,
      });

      onClose();
      refetchGraphs();
    } catch (err) {
      console.error("Failed to save temperature gauge properties:", err);
      setError("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const selectedLabel = labels.find(
    (label) => label.column_index === Number(columnIndex)
  );

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Temperature Gauge Properties</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
            <span className="ms-2">Loading parameters...</span>
          </div>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Temperature Parameter</Form.Label>
              <Form.Select
                value={columnIndex}
                onChange={(event) => setColumnIndex(event.target.value)}
                disabled={labels.length === 0}
              >
                {labels.length === 0 && (
                  <option value="">No parameters available</option>
                )}

                {labels.map((label) => (
                  <option
                    key={label.column_index}
                    value={label.column_index}
                  >
                    {label.label}
                    {label.unit ? ` (${label.unit})` : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Display Unit</Form.Label>
              <Form.Select
                value={unit}
                onChange={(event) =>
                  setUnit(event.target.value as "C" | "F")
                }
              >
                <option value="C">Celsius (°C) — Default</option>
                <option value="F">Fahrenheit (°F)</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Sensor readings are treated as Celsius and converted for
                display when Fahrenheit is selected.
              </Form.Text>
            </Form.Group>

            <div className="row">
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Minimum (°C)</Form.Label>
                  <Form.Control
                    type="number"
                    value={minTempC}
                    onChange={(event) => setMinTempC(event.target.value)}
                  />
                </Form.Group>
              </div>

              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Maximum (°C)</Form.Label>
                  <Form.Control
                    type="number"
                    value={maxTempC}
                    onChange={(event) => setMaxTempC(event.target.value)}
                  />
                </Form.Group>
              </div>
            </div>

            {selectedLabel?.unit &&
              !["C", "°C", "F", "°F"].includes(selectedLabel.unit) && (
                <Alert variant="warning" className="mb-0">
                  The selected parameter has unit "{selectedLabel.unit}".
                  Please confirm it represents a Celsius temperature before
                  applying temperature conversion.
                </Alert>
              )}
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading || saving || labels.length === 0}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default TemperatureGaugeProperties;
